import { PropsWithChildren, useEffect } from 'react';

import crypto from 'crypto';

import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { TermOffered } from '~apis/dashboard';
import { CompanyUserPermission } from '~apis/role/type';
import { useCompany, useCompanyMember } from '~hooks/queries/companies/query';
import { useGetFinanceInfo } from '~hooks/queries/dashboard/query';
import { useUserPermissions } from '~hooks/queries/role/query';
import { useGetSettings } from '~hooks/queries/setting/query';
import { useCompanyId } from '~hooks/useCompanyId';
import useIsDemoTrial from '~hooks/useIsDemoTrial';
import useSuperUserHook from '~hooks/useSuperUserHook';

import { update, shutdown, boot } from '.';

const ensurePolyfill = () => {
  return new Promise<void>((resolve) => {
    if (!window.requestIdleCallback) {
      window.requestIdleCallback = function (cb: IdleRequestCallback): number {
        return window.setTimeout(() => {
          const start = Date.now();
          cb({
            didTimeout: false,
            timeRemaining: function () {
              return Math.max(0, 50 - (Date.now() - start));
            },
          });
        }, 1);
      };
    }

    if (!window.cancelIdleCallback) {
      window.cancelIdleCallback = function (id: number) {
        clearTimeout(id);
      };
    }

    resolve();
  });
};

export const IntercomProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const session = useSuperUserHook();
  const user = session?.data;
  const { companyId } = useCompanyId();
  const { data: userPermissionsData } = useUserPermissions();
  const userPermissions = userPermissionsData?.Data?.data?.companyUserPermissions ?? [];

  const { data } = useGetFinanceInfo({
    companyId,
    checkPermission: true,
    permissionEnabled: userPermissions.includes(CompanyUserPermission.CAN_MANAGE_CLOBE_FINANCE),
  });

  const financeInfo = data?.Data?.data?.items?.find(
    (item) => item?.type === 'TERM_OFFERED',
  ) as TermOffered;

  const payoutLimit = financeInfo?.payoutLimit;

  const { data: companyUserDetail } = useCompanyMember({ companyId, memberId: user?.userId ?? '' });
  const userDetail = companyUserDetail?.Data?.data;

  const { data: companyData } = useCompany({ companyId });
  const { data: settingsData } = useGetSettings();
  const step = settingsData?.Data?.data?.onboardingStep;

  const companyDetail = companyData?.Data?.data;

  const userAuthenticated = user && session?.status === 'authenticated';
  const isSuperUser = Boolean(session?.data?.isAdmin);
  const isSupportUser = session?.data?.iss === 'SUPPORT';
  const isDemoTrial = useIsDemoTrial();
  if (
    typeof window !== 'undefined' &&
    !window.Intercom &&
    !isSuperUser &&
    !isSupportUser &&
    !isDemoTrial
  ) {
    boot();
  }

  useEffect(() => {
    const handleRouteChange = () => {
      if (typeof window !== 'undefined') {
        update();
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (!userAuthenticated) {
      boot({
        hide_default_launcher: false,
      });
    }
    const isSuperUser = Boolean(session?.data?.isAdmin);
    const isSupportUser = session?.data?.iss === 'SUPPORT';

    const userId = session?.data?.userId;
    async function track() {
      if (!process.env.INTERCOM_SECRET || !userId) return;

      if (userId) {
        const user_hash = crypto
          .createHmac('sha256', process.env.INTERCOM_SECRET as string)
          .update(userId)
          .digest('hex');

        // https://app.intercom.com/a/apps/lb92dhfx/settings/identity-verification/web
        boot({
          user_id: userId,
          user_hash, // an Identity Verification user has
          hide_default_launcher: true,
        });

        update({
          name: `${user?.userName}`,
        });
      } else {
        shutdown();
      }
    }

    if (!isSuperUser && !isSupportUser && !isDemoTrial) {
      ensurePolyfill().then(() => {
        requestIdleCallback(
          () => {
            track();
          },
          {
            timeout: 1000,
          },
        );
      });
    }
  }, [userAuthenticated, session?.data?.userId, isDemoTrial]);

  useEffect(() => {
    if (companyDetail && step) {
      ensurePolyfill().then(() => {
        requestIdleCallback(
          () => {
            update({
              company: {
                id: companyDetail.id,
                name: companyDetail.name,
                created_at: dayjs(companyDetail.createdAt).unix(),
                website: companyDetail.website,
                plan: step,
                company_payout_limit: payoutLimit ? payoutLimit.toLocaleString() : '',
                size: payoutLimit ?? 0,
                industry: `${companyDetail.serviceName} ${
                  companyDetail.korRepresentativeName
                    ? `(대표자: ${companyDetail.korRepresentativeName})`
                    : ''
                }`,
              },
            });
          },
          { timeout: 1000 },
        );
      });
    }
  }, [companyDetail, step, payoutLimit]);

  useEffect(() => {
    if (userDetail) {
      ensurePolyfill().then(() => {
        requestIdleCallback(
          () => {
            update({
              email: userDetail.email,
            });
          },
          { timeout: 1000 },
        );
      });
    }
  }, [userDetail]);

  useEffect(() => {
    if (companyDetail?.name) {
      ensurePolyfill().then(() => {
        requestIdleCallback(
          () => {
            update({
              current_company_name: companyDetail.name,
            });
          },
          { timeout: 1000 },
        );
      });
    }
  }, [companyDetail?.name]);

  return children;
};
